/**
 * ScoringRulesModal — iter87.180 Item B.
 *
 * Info popup rendered from the Leaderboard tile header info icon. Shows
 * the ACTIVE scoring rules for the current tournament, sourced live
 * from `tournament.scoring` + related tournament fields so it always
 * mirrors reality — nothing hardcoded.
 *
 * Sections rendered are template-aware so an operator on a boost-only
 * tournament isn't reading about group-stage draws they'll never see.
 */
import { X, Info, Trophy, Zap, Crown, Flag, ListChecks, Swords } from "lucide-react";
import { Baseball, Basketball } from "@phosphor-icons/react";
import { isBoostOnlyTemplate } from "@/lib/boostOnlyTemplates";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";

export default function ScoringRulesModal({ open, onClose, tournament }) {
    if (!open) return null;
    const t = tournament || {};
    const s = t.scoring || {};
    const tpl = t.template || "";
    const fmt = (t.format || "").toLowerCase();
    const isBoostOnly = isBoostOnlyTemplate(tpl);
    const isSeason = fmt === "round_robin";
    const isGolf = fmt === "golf";
    const isMlb = tpl === "mlb_playoffs_api";
    // iter87.225 — NBA Playoffs, sibling of MLB above.
    const isNba = tpl === "nba_playoffs_api";
    // iter87.362 — NWSL Playoffs: pure pick'em, no draft/roster at all.
    const isNwsl = tpl === "nwsl_playoffs_api";
    // iter87.465.20 — MLS Playoffs: pure pick'em too, but ALSO has a
    // Round One Series Predictor (MLB/NBA-shaped) + a Play-In Bonus
    // (pre-season primitive) alongside Champion/Dark Horse.
    const isMls = tpl === "mls_playoffs_api";
    // iter87.466.1 — UCL Playoffs: pure pick'em too. Champion + Dark
    // Horse (pre-season) + Advance Predictor (every 2-leg tie,
    // in-season) + Champions Predictor (Final only, in-season, fixed
    // 10/5/5 — NOT the generic boost section below).
    const isUcl = tpl === "ucl_playoffs_api";
    // iter87.373 → iter87.374 → iter87.437 — both NFL and CFB
    // Playoffs have now retired their Team Pick Pot drafts in favor of
    // a pure pick'em: National/Super Bowl Champion (1-2 picks, hedge
    // cost) + Dark Horse Pick(s), see the sections below.
    const isFootballPlayoffs = isFootballPlayoffsTemplate(tpl);
    const isCfbPo = tpl === "cfb_playoffs_api";
    const isNflPo = tpl === "nfl_playoffs_api";
    // iter87.238 (user-reported) — NFL Season Pick'em's Matchday Boost
    // sidecar renders as "Gameday Boost" everywhere else (see
    // MatchdayBoostModal.jsx iter87.122) — this modal's title had been
    // left on the generic "Matchday Boost" wording.
    const isNfl = tpl === "nfl_season_api";
    // iter87.244 (user-reported) — "match"/"matchday" is soccer
    // terminology; every football template (NFL Season sidecar, NFL
    // Weekly Spread Boost, NFL/CFB Playoffs Weekly Spread) should read
    // "game"/"week" instead. Single flag reused everywhere below.
    const isFootball = isNfl || tpl === "nfl_boost_api" || isFootballPlayoffsTemplate(tpl);
    // iter87.222 (user-reported) — MLB never uses the generic
    // bracket-advancement engine (see `mlb_engine.py`'s per-game win/loss
    // model, iter87.213.9), so it must never show the generic
    // "Team Advancement Points" ladder even though it happens to carry
    // `stages` rows for bracket-tracking display purposes elsewhere.
    const hasKO = !isMlb && !isNba && !isNwsl && !isMls && !isUcl && !isFootballPlayoffs && (t.stages || []).some((st) => st.key && st.key !== "group" && st.key !== "champion");
    const boostEnabled = !!t?.matchday_boost?.enabled;
    const powerEnabled = !!t?.power_pick_enabled;
    // iter87.199.127 — Mirror `scoring_helpers.py`'s actual payout gate
    // (mode === "pot" AND the creation-time `champions_pick_bonus` stamp)
    // instead of falling through to `scoring.champion_points` on its own.
    // Domestic league templates (Club Soccer etc.) carry a leftover
    // `champion_points: 20` that the real scoring engine never pays out —
    // showing it here was pure confusion (operator-reported).
    const pickemCfg = t?.pickem || {};
    const champBonus = (pickemCfg.mode === "pot" && pickemCfg.champions_pick_bonus)
        ? (s.champion_points || pickemCfg.champions_pick_bonus || 0)
        : 0;
    const cumulative = s.cumulative_advancement !== false;

    // Season W/D/L points (soccer clubs, group_knockout, round_robin).
    // iter87.465.22 (testing-agent-caught) — MLS Playoffs tournaments
    // are stored with format "group_knockout" (shared infra with
    // regular soccer seasons), so this was wrongly showing "Match
    // Result Points" (Win 3 / Draw 1 / Loss 0) for a pure pick'em
    // playoff template with no team ownership at all. iter87.479
    // (user-reported) — UCL Playoffs shares the same "group_knockout"
    // storage format and has the exact same pure pick'em shape (no team
    // ownership), but was missed from this exclusion list when it was
    // added — same fix, added here.
    const showSeasonWDL = !isBoostOnly && !isGolf && !isMlb && !isNba && !isNwsl && !isMls && !isUcl && !isFootballPlayoffs
        && (isSeason || fmt === "group_knockout" || tpl === "world_cup" || tpl === "world_cup_api");

    // Team advancement scale (KO templates).
    const stageRows = [
        { key: "reach_r64_points",   label: "Reach Round of 64" },
        { key: "reach_r32_points",   label: "Reach Round of 32" },
        { key: "reach_r16_points",   label: "Reach Round of 16" },
        { key: "reach_qf_points",    label: "Reach Quarterfinal" },
        { key: "reach_sf_points",    label: "Reach Semifinal" },
        { key: "reach_final_points", label: "Reach Final" },
        { key: "champion_points",    label: "Win the Championship" },
    ].filter((r) => {
        // Only render stages that exist on this tournament OR non-zero.
        const stageKey = r.key.replace("reach_", "").replace("_points", "");
        const stageExists = (t.stages || []).some((st) => st.key === stageKey);
        return stageExists || (stageKey === "champion" && s[r.key]);
    });

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="scoring-rules-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-lg w-full max-h-[88dvh] flex flex-col"
                data-testid="scoring-rules-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <Info className="w-5 h-5 text-cyber shrink-0" />
                        <h2 className="font-display font-black text-xl uppercase truncate">
                            Scoring Rules
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="scoring-rules-close-btn"
                        aria-label="Close scoring rules"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-4 flex-1 text-sm text-zinc-300 leading-relaxed space-y-5">
                    {showSeasonWDL && (
                        <RuleSection icon={Trophy} title={isFootball ? "Game Result Points" : "Match Result Points"} testId="scoring-rules-wdl">
                            <RuleRow label="Win" value={s.win_points ?? 0} />
                            <RuleRow label="Draw" value={s.draw_points ?? 0} />
                            <RuleRow label="Loss" value={s.loss_points ?? 0} />
                            {s.goal_points ? <RuleRow label="Goal scored" value={s.goal_points} /> : null}
                            {s.clean_sheet_points ? <RuleRow label="Clean sheet" value={s.clean_sheet_points} /> : null}
                        </RuleSection>
                    )}

                    {hasKO && stageRows.length > 0 && (
                        <RuleSection icon={ListChecks} title="Team Advancement Points" testId="scoring-rules-advancement">
                            {stageRows.map((r) => (
                                <RuleRow key={r.key} label={r.label} value={s[r.key] ?? 0} />
                            ))}
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                {cumulative
                                    ? "Cumulative — a team at the semifinal has earned every prior stage's points."
                                    : "Final position — only the team's furthest stage counts."}
                            </p>
                        </RuleSection>
                    )}

                    {champBonus > 0 && (
                        <RuleSection icon={Crown} title="Champion Bonus" testId="scoring-rules-champion">
                            <RuleRow label="Correct champion pick" value={champBonus} />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Awarded when the team / club / player you named as champion actually wins it.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.478 (user-reported — "power pick is first
                        and then game scoring in baseball playoffs") —
                        MLB/NBA render their own Power Pick section
                        right after Champions Pick (see below), matching
                        the dashboard to-do tile order (champion before
                        power). This generic block now only covers every
                        OTHER power-pick template (World Cup / NFL
                        Season Super Draft / Custom pot pick'em etc.),
                        where it already sat correctly right after
                        Champion Bonus. */}
                    {powerEnabled && !isMlb && !isNba && (
                        <RuleSection icon={Zap} title="Power Pick Multiplier" testId="scoring-rules-power">
                            <p className="text-xs text-zinc-300">
                                Pick one of your drafted teams to be your Power team. All of that team's points are multiplied by the Power multiplier locked in at kickoff.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.437 (user-requested — "mirror NFL exactly")
                        — CFB Playoffs: National Champion allows 1 OR 2
                        picks, with a hedging penalty, same shape as NFL's
                        Super Bowl Champion above. */}
                    {isCfbPo && (
                        <RuleSection icon={Crown} title="National Champion" testId="scoring-rules-cfb-champion">
                            <RuleRow label="Pick 1 team — it wins the national championship" value={`+${t?.scoring?.cfb_champion_points ?? 20}`} />
                            <RuleRow label="Pick 2 teams — one of them wins" value={`+${t?.scoring?.cfb_champion_hedge_points ?? 8}`} />
                            <RuleRow label="Neither pick wins" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick 1 or 2 of the 12 playoff teams. Hedging with 2 picks costs you vs locking in 1. Locks the instant the First Round kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.437 — CFB Playoffs Dark Horse: ONE pick
                        (not NFL's two per-conference) — CFB's 12-team
                        field isn't cleanly 2-way groupable like NFL's
                        AFC/NFC. iter87.450 — tightened to seed 7-12. */}
                    {isCfbPo && (
                        <RuleSection icon={Swords} title="Dark Horse Pick" testId="scoring-rules-cfb-darkhorse">
                            <RuleRow label="Each game your Dark Horse wins" value={`+${t?.scoring?.cfb_darkhorse_win_points ?? 8}`} />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick 1 team (seed 7-12 only — seeds 1-6 are out). Points stack across every round that team wins. Locks the instant the First Round kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.374 (user-requested — "follow the NWSL
                        format ... remove Power picks") — NFL Playoffs:
                        Super Bowl Champion allows 1 OR 2 picks (NWSL
                        allows only 1), with a hedging penalty. */}
                    {isNflPo && (
                        <RuleSection icon={Crown} title="Super Bowl Champion" testId="scoring-rules-nfl-champion">
                            <RuleRow label="Pick 1 team — it wins the Super Bowl" value={`+${t?.scoring?.nfl_champion_points ?? 20}`} />
                            <RuleRow label="Pick 2 teams — one of them wins" value={`+${t?.scoring?.nfl_champion_hedge_points ?? 8}`} />
                            <RuleRow label="Neither pick wins" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick 1 or 2 of the 14 playoff teams. Hedging with 2 picks costs you vs locking in 1. Locks the instant Wild Card Weekend kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.374 — NFL Playoffs Dark Horse: TWO
                        independent picks (one per conference), each
                        restricted to a wild-card seed (5-7, not a
                        division winner) — vs NWSL's single seed-5-8 pick. */}
                    {isNflPo && (
                        <RuleSection icon={Swords} title="Dark Horse Picks" testId="scoring-rules-nfl-darkhorse">
                            <RuleRow label="Each game your AFC Dark Horse wins" value={`+${t?.scoring?.nfl_darkhorse_win_points ?? 4}`} />
                            <RuleRow label="Each game your NFC Dark Horse wins" value={`+${t?.scoring?.nfl_darkhorse_win_points ?? 4}`} />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick 1 wild-card team (seed 5-7 — not a division winner) per conference. Points stack across every round that team wins. Locks the instant Wild Card Weekend kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {isMlb && (
                        <RuleSection icon={Baseball} title="Team Pick Pot" testId="scoring-rules-mlb-pot">
                            <RuleRow label="Your rostered team wins a game" value="+6" />
                            <RuleRow label="Your rostered team loses a game" value="−2" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Every individual postseason game counts — not just series wins. Multiplied by your Power Pick's pot tier (1x / 1.25x / 1.5x) if that team is playing.
                            </p>
                        </RuleSection>
                    )}

                    {isMlb && (
                        <RuleSection icon={Crown} title="Champions Pick" testId="scoring-rules-mlb-champs">
                            <RuleRow label="Your pick wins the World Series" value="+10" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                One pick, locked once the World Series matchup is set. No points for anything short of the title.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.478 — moved here from the generic early
                        slot (was rendering before Team Pick Pot /
                        Champions Pick, which read as "power pick first,
                        then game scoring" — the user's exact complaint). */}
                    {isMlb && powerEnabled && (
                        <RuleSection icon={Zap} title="Power Pick Multiplier" testId="scoring-rules-power">
                            <p className="text-xs text-zinc-300">
                                Pick one of your rostered teams to be your Power team. Every game it plays scores at its pot's multiplier instead of 1x.
                            </p>
                        </RuleSection>
                    )}

                    {isMlb && (
                        <RuleSection icon={Baseball} title="Series Predictor" testId="scoring-rules-mlb-series">
                            <RuleRow label="Correct series winner" value="+6" />
                            <RuleRow label="Winner AND exact deciding game" value="+10" />
                            <RuleRow label="Wrong series winner" value="−2" />
                            <RuleRow label="Wrong winner but exact deciding game" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick who advances each series + which game it ends on (Wild Card, Division, Championship, World Series). Locks per-series once that round's matchups are set.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.225 — NBA Playoffs, sibling of the MLB
                        sections above (one round deeper: Play-In → First
                        Round → Conf Semis → Conf Finals → NBA Finals). */}
                    {isNba && (
                        <RuleSection icon={Basketball} title="Team Pick Pot" testId="scoring-rules-nba-pot">
                            <RuleRow label="Your rostered team wins a game" value="+6" />
                            <RuleRow label="Your rostered team loses a game" value="−2" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Every individual playoff game counts — including Play-In — not just series wins. Multiplied by your Power Pick's pot tier (1x / 1.25x / 1.5x / 2x) if that team is playing.
                            </p>
                        </RuleSection>
                    )}

                    {isNba && (
                        <RuleSection icon={Crown} title="Champions Pick" testId="scoring-rules-nba-champs">
                            <RuleRow label="Your pick wins the NBA Finals" value="+10" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                One pick, locked once the Conference Finals matchup is set. No points for anything short of the title.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.478 — sibling of the MLB move above. */}
                    {isNba && powerEnabled && (
                        <RuleSection icon={Zap} title="Power Pick Multiplier" testId="scoring-rules-power">
                            <p className="text-xs text-zinc-300">
                                Pick one of your rostered teams to be your Power team. Every game it plays scores at its pot's multiplier instead of 1x.
                            </p>
                        </RuleSection>
                    )}

                    {isNba && (
                        <RuleSection icon={Basketball} title="Series Predictor" testId="scoring-rules-nba-series">
                            <RuleRow label="Correct series winner" value="+6" />
                            <RuleRow label="Winner AND exact deciding game" value="+10" />
                            <RuleRow label="Wrong series winner" value="−2" />
                            <RuleRow label="Wrong winner but exact deciding game" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick who advances each series + which game it ends on (First Round, Conference Semifinals, Conference Finals, NBA Finals — the Play-In field is excluded, it's single-elimination not a series). Locks per-series once that round's matchups are set.
                            </p>
                        </RuleSection>
                    )}

                    {isNwsl && (
                        <RuleSection icon={Crown} title="NWSL Champion" testId="scoring-rules-nwsl-champ">
                            <RuleRow label="Pick 1 team — it wins it all" value={`+${s.nwsl_champion_points ?? 15}`} />
                            <RuleRow label="Pick 2 teams — one of them wins" value={`+${s.nwsl_champion_hedge_points ?? 5}`} />
                            <RuleRow label="Neither pick wins" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick 1 or 2 of the 8 bracket teams. Hedging with 2 picks costs you vs locking in 1. Locked the instant Round 1 (Quarterfinals) kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {isNwsl && (
                        <RuleSection icon={Flag} title="Dark Horse Pick" testId="scoring-rules-nwsl-darkhorse">
                            <RuleRow label="Per playoff win" value={`+${s.nwsl_darkhorse_win_points ?? 5}`} />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Must be a seed 5-8 team — locks alongside NWSL Champion at Round 1 kickoff. Stacks for every game it wins.
                            </p>
                        </RuleSection>
                    )}

                    {isNwsl && (
                        <RuleSection icon={ListChecks} title="Playoff Predictor" testId="scoring-rules-nwsl-predictor">
                            <RuleRow label="Correct winner" value="+3" />
                            <RuleRow label="Correct result (Regulation / Extra Time / Penalties)" value="+2" />
                            <RuleRow label="Total goals over/under 2.5" value="+5" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                No draft, no rostered teams — just 3 quick picks per game. Each game locks at its own kickoff.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.465.20 — MLS Playoffs (user-approved
                        spec): pure pick'em, no draft. Champions Pick +
                        Dark Horse Pick + Play-In Bonus all lock together
                        at Wild Card kickoff; Series Predictor covers
                        Round One (best-of-3) only; Conf Semis/Finals/
                        MLS Cup reuse the Match Predictor section above. */}
                    {isMls && (
                        <RuleSection icon={Crown} title="MLS Champion" testId="scoring-rules-mls-champion">
                            <RuleRow label="Pick 1 team — it wins the MLS Cup" value={`+${s.mls_champion_points ?? 15}`} />
                            <RuleRow label="Pick 2 teams — one of them wins" value={`+${s.mls_champion_hedge_points ?? 5}`} />
                            <RuleRow label="Neither pick wins" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick 1 or 2 playoff-field teams (any conference). Hedging with 2 picks costs you vs locking in 1. Locked the instant either conference's Wild Card game kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {isMls && (
                        <RuleSection icon={Swords} title="Dark Horse Pick" testId="scoring-rules-mls-darkhorse">
                            <RuleRow label="Per playoff win" value={`+${s.mls_darkhorse_win_points ?? 5}`} />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Must be a seed 6-9 team (own conference) — locks alongside MLS Champion/Play-In at Wild Card kickoff. Stacks for every game it wins.
                            </p>
                        </RuleSection>
                    )}

                    {isMls && (
                        <RuleSection icon={ListChecks} title="Play-In Bonus" testId="scoring-rules-mls-playin">
                            <RuleRow label="Each conference's Wild Card winner, correctly picked" value={`+${s.mls_playin_correct_points ?? 6}`} />
                            <RuleRow label="Each conference's Wild Card winner, picked wrong" value={`${s.mls_playin_incorrect_points ?? -2}`} />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                One pick per conference (East + West). Flat bonus, separate from the in-season Series Predictor below. Locks alongside MLS Champion/Dark Horse at kickoff.
                            </p>
                        </RuleSection>
                    )}

                    {isMls && (
                        <RuleSection icon={Trophy} title="Series Predictor" testId="scoring-rules-mls-series">
                            <RuleRow label="Correct series winner" value="+6" />
                            <RuleRow label="Winner AND exact deciding game" value="+10" />
                            <RuleRow label="Wrong series winner" value="−2" />
                            <RuleRow label="Wrong winner but exact deciding game" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick who advances + which game it ends on, for every Round One series (best-of-3 only, 16 teams total — Wild Card is single-game, Conf Semis+ use the Playoff Predictor below). Each series locks the instant its own Game 1 kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {/* iter87.466.1 — UCL Playoffs (user-approved
                        spec): pure pick'em, no draft. Champion + Dark
                        Horse lock together at Knockout Round Play-offs
                        kickoff. Advance Predictor covers every 2-leg
                        tie (Knockout through Semis). Champions
                        Predictor is the Final's own fixed-scoring
                        3-sub-pick — NOT the generic boost section. */}
                    {isUcl && (
                        <RuleSection icon={Crown} title="UCL Champion" testId="scoring-rules-ucl-champion">
                            <RuleRow label="Pick 1 team — it wins the Final" value={`+${s.ucl_champion_points ?? 20}`} />
                            <RuleRow label="Pick 2 teams — one of them wins" value={`+${s.ucl_champion_hedge_points ?? 8}`} />
                            <RuleRow label="Neither pick wins" value="0" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Pick 1 or 2 teams from the seed 1-24 field. Hedging with 2 picks costs you vs locking in 1. Locked the instant the Knockout Round Play-offs kick off.
                            </p>
                        </RuleSection>
                    )}

                    {isUcl && (
                        <RuleSection icon={Swords} title="Dark Horse Pick" testId="scoring-rules-ucl-darkhorse">
                            <RuleRow label="Seed 17-24, per tie advanced" value={`+${s.ucl_darkhorse_outer_tier_points ?? 10}`} />
                            <RuleRow label="Seed 9-16, per tie advanced" value={`+${s.ucl_darkhorse_inner_tier_points ?? 5}`} />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Must be a seed 9-24 team (seeds 1-8 are byes, not underdogs) — locks alongside UCL Champion at Knockout Round Play-offs kickoff. Stacks for every tie it advances.
                            </p>
                        </RuleSection>
                    )}

                    {isUcl && (
                        <RuleSection icon={ListChecks} title="Two-Leg Predictor" testId="scoring-rules-ucl-advance">
                            <RuleRow label="Knockout + Round of 16 — correct" value="+3" />
                            <RuleRow label="Knockout + Round of 16 — incorrect" value="−1" />
                            <RuleRow label="Quarter-finals + Semi-finals — correct" value="+6" />
                            <RuleRow label="Quarter-finals + Semi-finals — incorrect" value="−2" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                One pick per 2-leg tie: who advances the aggregate. Locks before that tie's Leg 1 kicks off.
                            </p>
                        </RuleSection>
                    )}

                    {isUcl && (
                        <RuleSection icon={Trophy} title="Champions Predictor" testId="scoring-rules-ucl-champions-predictor">
                            <RuleRow label="Correct Final winner" value="+10" />
                            <RuleRow label="Correct Regulation / Extra Time / Penalties" value="+5" />
                            <RuleRow label="Correct total goals over/under 2.5" value="+5" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                The Final only — 3 independent picks, locks at kickoff.
                            </p>
                        </RuleSection>
                    )}

                    {(boostEnabled || isBoostOnly) && (
                        <RuleSection icon={Flag} title={isMls ? "Playoff Predictor" : (isFootballPlayoffsTemplate(tpl) ? "Playoff Predictor" : (isBoostOnly ? "Weekly Pick'em (Spread)" : (isNfl ? "Gameday Boost" : "Matchday Boost")))} testId="scoring-rules-boost">
                            <RuleRow label="Pick wins straight up AND covers the spread" value="+6" />
                            <RuleRow label="Pick wins straight up but doesn't cover" value="+2" />
                            <RuleRow label="Pick loses but covers the spread" value="0" />
                            <RuleRow label="Pick loses AND doesn't cover" value="−2" />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                {isMls
                                    ? "Same rules for every Conf Semis, Conf Finals and MLS Cup game."
                                    : <>Same rules for every {isFootball ? "week" : "week / matchday"}. Totals across the season decide the {isBoostOnly ? "entire" : "boost"} standings.</>}
                            </p>
                            {/* iter87.199.9 — Ties grade as losses (very
                                American view — a tie is like kissing your
                                sister, apparently). Push against the
                                spread is treated as "did not cover".
                                iter87.465.21 — MLS Conf Semis+ are
                                single-elimination (extra time + penalties
                                settle any draw), so the "American tie"
                                joke doesn't land — swap in neutral copy. */}
                            {isMls ? (
                                <p data-testid="scoring-rules-boost-ties-note" className="text-[11px] text-zinc-500 pt-1 italic">
                                    These knockout rounds always have a winner (extra time + penalties if needed). A push against the spread is graded as "did not cover."
                                </p>
                            ) : (
                                <p data-testid="scoring-rules-boost-ties-note" className="text-[11px] text-zinc-500 pt-1 italic">
                                    Ties count as losses — a very American view of the sport 🇺🇸. A push against the spread is graded as "did not cover."
                                </p>
                            )}
                            {/* iter87.370 — Playoff Predictor: a second,
                                independently-locked Over/Under pick on
                                every game you select against the spread. */}
                            {(isFootballPlayoffsTemplate(tpl) || isMls) && (
                                <>
                                    <RuleRow label="Over/Under · correct" value="+4" />
                                    <RuleRow label="Over/Under · miss (a push counts as a miss)" value="−2" />
                                    <p data-testid="scoring-rules-ou-note" className="text-[11px] text-zinc-500 pt-1 italic">
                                        Each game's spread AND Over/Under line locks the moment you make that pick — editable, re-locking the then-current line, up until kickoff.
                                    </p>
                                </>
                            )}
                        </RuleSection>
                    )}

                    {/* iter87.195.1 — Trophy Tokens as its own callout, placed
                        after Weekly Pick'em / Matchday Boost per operator ask.
                        Renders only when the tournament has the feature on. */}
                    {tournament?.trophy_tokens_preview?.enabled && (
                        <RuleSection icon={Trophy} title="Trophy Tokens" testId="scoring-rules-trophy-tokens">
                            <RuleRow
                                label="Winner also receives"
                                value={`+${tournament.trophy_tokens_preview.winner_pot} Trophy Tokens`}
                            />
                            <p className="text-[11px] text-zinc-500 pt-1.5">
                                Accumulate Trophy Tokens across tournaments in your rivalry — a season-long side-pot on top of the tournament leaderboard. Buy-in {tournament.trophy_tokens_preview.allocation_mode === "per_player"
                                    ? `${tournament.trophy_tokens_preview.amount} tokens per crewmate`
                                    : `${tournament.trophy_tokens_preview.amount} tokens (fixed pot)`}
                                {" · "}{tournament.trophy_tokens_preview.n_opted_in} in the pool
                                {tournament.trophy_tokens_preview.withhold_pct > 0 && ` · ${tournament.trophy_tokens_preview.withhold_pct}% held back`}
                                . {tournament.trophy_tokens_preview.finalized
                                    ? "Awards finalized."
                                    : tournament.trophy_tokens_preview.locked
                                        ? "Roster locked."
                                        : "Roster locks at first kickoff / R1 tee-off — pot moves until then."}
                            </p>
                        </RuleSection>
                    )}

                    {isGolf && (
                        <RuleSection icon={Trophy} title="Golf Pick'em" testId="scoring-rules-golf">
                            <p className="text-xs text-zinc-300">
                                Points come from your picked golfers' live position vs. par each round. Best-round
                                and cut-line rules are set per tournament — check the round header for the active tier.
                            </p>
                        </RuleSection>
                    )}
                </div>

                <div className="p-3 border-t-2 border-[#222] flex justify-end bg-ink">
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-primary text-xs py-2"
                        data-testid="scoring-rules-done-btn"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
}

function RuleSection({ icon: Icon, title, testId, children }) {
    return (
        <div className="space-y-2" data-testid={testId}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-cyber" />
                <h3 className="font-display font-black text-sm uppercase tracking-tight text-white">
                    {title}
                </h3>
            </div>
            <div className="space-y-1 pl-1">{children}</div>
        </div>
    );
}

function RuleRow({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3 text-xs">
            <span className="text-zinc-300">{label}</span>
            <span className="font-mono font-bold text-cyber tabular-nums">{value}</span>
        </div>
    );
}
